import { get, off, ref, runTransaction, set, update } from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import { db } from "../lib/firebase";
import { getPathDatabaseByRole } from "./auth";
import { LaunchError } from "../utils/errors";
import { getCreativeJobSentryFailure } from "../utils/creativeJobSentryReport";
import { store } from "../app/store";

import {
  isCreativeNotification,
  mapVisibleCreativeJobsToNotifications,
  normalizeCreativeJobs,
  CREATIVE_JOB_RTDB_SOURCE,
  getCreativeJobAlertMessageKey,
} from "../utils/creativeJobsNotifications";
import {
  clearCreativeJobProgress,
  migrateCreativeJobProgress,
} from "../utils/creativeJobProgressStorage";
import {
  addNotification,
  promoteCreativeNotification,
  removeNotification,
  syncCreativeJobNotificationsAction,
} from "./alerts";
import { createCreativeJobAndWaitForResponse } from "./jobs";
import { ALERTS_ASYNC_STATUS } from "../utils/constants";
import { getCreativeJobAlertConfig } from "../utils/creativeJobAlertConfig";
import { filterContentsNotInContentList, startAddContentsToList } from "./user";

const persistClaimsInFlight = new Set();

const releaseCreativeJobPersistClaim = async ({ jobID, claimRef }) => {
  persistClaimsInFlight.delete(jobID);

  if (!claimRef) return;

  try {
    await set(claimRef, null);
  } catch (error) {
    console.error("Error releasing creative job persist claim", error);
    store.dispatch(
      LaunchError(error, false, [], {
        functionName: "releaseCreativeJobPersistClaim",
        capture: "exception",
        context: { jobID },
        tags: { source: "rtdb", action: "creativeJobs" },
      })
    );
  }
};

export { isCreativeNotification };

export const startListenerCreativeJobs =
  ({
    advertiserID,
    onUpdatedDataCallback = () => {},
    onReferenceAvailable = () => {},
  }) =>
  async (dispatch) => {
    try {
      if (!advertiserID) {
        onUpdatedDataCallback([]);
        return false;
      }

      const dbRef = ref(db, `jobs/${advertiserID}`);
      onReferenceAvailable(dbRef);

      onValue(dbRef, (snapshot) => {
        const jobs = normalizeCreativeJobs(snapshot.val());
        onUpdatedDataCallback(jobs);
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerCreativeJobs",
          tags: { action: "creativeJobs" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerCreativeJobs =
  ({ listenerRef } = {}) =>
  async () => {
    if (!listenerRef) return;
    await off(listenerRef);
  };

export const dismissCreativeJob =
  ({ advertiserID, jobID }) =>
  async (dispatch) => {
    try {
      if (!advertiserID || !jobID) return { ok: false };

      const jobRef = ref(db, `jobs/${advertiserID}/${jobID}`);
      const snapshot = await get(jobRef);

      if (!snapshot.exists()) {
        return { ok: true, skipped: true };
      }

      const dismissedAt = Date.now();
      const jobData = snapshot.val();

      await update(ref(db), {
        [`jobs/${advertiserID}/${jobID}`]: null,
        [`jobsDismissed/${advertiserID}/${jobID}`]: {
          ...jobData,
          dismissedAt,
          updateTime: dismissedAt,
        },
      });

      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "dismissCreativeJob",
          tags: { action: "creativeJobs" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const syncCreativeJobNotifications = (jobs) => (dispatch) => {
  const creativeJobNotifications = mapVisibleCreativeJobsToNotifications(jobs);
  dispatch(syncCreativeJobNotificationsAction(creativeJobNotifications));
};

export const reportCreativeJobError = (job, advertiserID) => (dispatch) => {
  const failure = getCreativeJobSentryFailure(job);
  if (!failure) return;

  dispatch(
    LaunchError(
      {
        ok: false,
        message: failure.message,
        code: failure.code,
        creativeJobID: failure.jobID,
        creativeJobAdvertiserID: advertiserID,
      },
      false,
      [],
      {
        functionName: failure.jobType || "creativeJob",
        creativeJobType: failure.jobType,
        context: failure.context,
        tags: { action: "creativeJobs", source: "job" },
        level: "error",
        capture: "exception",
      }
    )
  );
};

export const promoteCreativeJobNotification =
  ({ fromId, jobID }) =>
  (dispatch) => {
    migrateCreativeJobProgress({ fromKey: fromId, toKey: jobID });
    dispatch(promoteCreativeNotification({ fromId, jobID }));
  };

export const addCreativeJobLoadingNotification =
  ({ jobID, jobType, generationData }) =>
  (dispatch) => {
    const config = getCreativeJobAlertConfig(jobType);

    dispatch(
      addNotification({
        id: jobID,
        jobID,
        source: CREATIVE_JOB_RTDB_SOURCE,
        type: config.alertType,
        status: ALERTS_ASYNC_STATUS.LOADING,
        progress: 0,
        jobType,
        messageKey: getCreativeJobAlertMessageKey({ jobType }),
        generationData: {
          modalType: generationData?.modalType || config.modalType,
          mediaType: generationData?.mediaType || config.mediaType,
          isGenerating: true,
          ...generationData,
        },
      })
    );
  };

export const runCreativeJobWithAlert = async ({
  dispatch,
  jobType,
  payload,
  generationData,
  timeoutMs,
  withAlert = true,
}) => {
  const config = getCreativeJobAlertConfig(jobType);
  const reopenModalType =
    payload.modalType || generationData?.modalType || config.modalType;
  const payloadWithReopen = reopenModalType
    ? { ...payload, modalType: reopenModalType }
    : payload;

  return createCreativeJobAndWaitForResponse({
    dispatch,
    jobType,
    payload: payloadWithReopen,
    timeoutMs,
    onJobCreated: ({ jobID }) => {
      if (!withAlert) return;

      dispatch(
        addCreativeJobLoadingNotification({
          jobID,
          jobType,
          generationData: {
            ...generationData,
            modalType: generationData?.modalType || payloadWithReopen.modalType,
          },
        })
      );
    },
  });
};

export const closeCreativeNotification = (alertId) => (dispatch, getState) => {
  const notifications = getState()?.alerts?.notifications || [];
  const notification = notifications.find((item) => item.id === alertId);
  const storageKey = notification?.jobID ?? alertId;
  const jobID = notification?.jobID;

  clearCreativeJobProgress(storageKey);
  dispatch(removeNotification(alertId));

  if (!jobID) return;

  const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
  dispatch(dismissCreativeJob({ advertiserID, jobID }));
};

export const claimCreativeJobResultsPersist =
  ({ jobID }) =>
  async (dispatch) => {
    if (!jobID) return { claimed: false };

    if (persistClaimsInFlight.has(jobID)) {
      return { claimed: false };
    }

    persistClaimsInFlight.add(jobID);

    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (!advertiserID) {
        persistClaimsInFlight.delete(jobID);
        return { claimed: false };
      }

      const claimRef = ref(
        db,
        `jobs/${advertiserID}/${jobID}/resultsPersistedAt`
      );
      const transactionResult = await runTransaction(claimRef, (current) =>
        current ? undefined : Date.now()
      );

      if (!transactionResult.committed) {
        persistClaimsInFlight.delete(jobID);
        return {
          claimed: false,
          resultsPersistedAt: transactionResult.snapshot.val(),
        };
      }

      return {
        claimed: true,
        resultsPersistedAt: transactionResult.snapshot.val(),
        claimRef,
      };
    } catch (error) {
      persistClaimsInFlight.delete(jobID);
      dispatch(
        LaunchError(error, false, [], {
          functionName: "claimCreativeJobResultsPersist",
          tags: { action: "creativeJobs" },
          level: "error",
        })
      );
      return { claimed: false };
    }
  };

export const markCreativeJobResultsPersisted =
  ({ jobID }) =>
  async (dispatch) => {
    try {
      if (!jobID) return { ok: false };

      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (!advertiserID) return { ok: false };

      const persistedAt = Date.now();
      await update(ref(db, `jobs/${advertiserID}/${jobID}`), {
        resultsPersistedAt: persistedAt,
      });

      return { ok: true, resultsPersistedAt: persistedAt };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "markCreativeJobResultsPersisted",
          tags: { action: "creativeJobs" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startPersistCreativeJobResults =
  ({ jobID, contents }) =>
  async (dispatch) => {
    if (!jobID || !Array.isArray(contents) || contents.length === 0) {
      return { ok: false, data: null };
    }

    const claimResult = await dispatch(
      claimCreativeJobResultsPersist({ jobID })
    );
    if (!claimResult.claimed) {
      return { ok: true, skipped: true, data: null };
    }

    const contentsToSave = await dispatch(
      filterContentsNotInContentList(contents)
    );

    if (contentsToSave.length === 0) {
      return { ok: true, skipped: true, data: null };
    }

    const savedData = await dispatch(startAddContentsToList(contentsToSave));
    if (!savedData) {
      await releaseCreativeJobPersistClaim({
        jobID,
        claimRef: claimResult.claimRef,
      });
      return { ok: false, data: null };
    }

    return { ok: true, data: savedData };
  };
